/* eslint-disable ember/no-classic-components */
import Component from "@ember/component";
import { fn, get } from "@ember/helper";
import { on } from "@ember/modifier";
import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import { classify } from "@ember/string";
import { trustHTML } from "@ember/template";
import { tagName } from "@ember-decorators/component";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import ComboBox from "discourse/select-kit/components/combo-box";
import { eq } from "discourse/truth-helpers";
import DModal from "discourse/ui-kit/d-modal";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";
import PollBreakdownChart from "discourse/plugins/poll/discourse/components/poll-breakdown-chart";
import PollBreakdownOption from "discourse/plugins/poll/discourse/components/poll-breakdown-option";

@tagName("")
export default class PollBreakdownModal extends Component {
  @service dialog;
  @service siteSettings;

  model = null;
  charts = null;
  groupedBy = null;
  highlightedOption = null;
  displayMode = "percentage";

  init() {
    this.set("groupedBy", this.groupableUserFields[0]?.id);

    this.fetchGroupedPollData();

    super.init(...arguments);
  }

  @computed("model.poll.title", "model.post.topic.title")
  get title() {
    return this.model?.poll?.title
      ? trustHTML(this.model?.poll?.title)
      : this.model?.post?.topic?.title;
  }

  get groupableUserFields() {
    return this.siteSettings.poll_groupable_user_fields
      .split("|")
      .filter(Boolean)
      .map((field) => {
        const transformed = field.split("_").filter(Boolean);

        if (transformed.length > 1) {
          transformed[0] = classify(transformed[0]);
        }

        return { id: field, label: transformed.join(" ") };
      });
  }

  @computed("model.poll.options")
  get totalVotes() {
    return this.model?.poll?.options?.reduce(
      (sum, option) => sum + option.votes,
      0
    );
  }

  fetchGroupedPollData() {
    return ajax("/polls/grouped_poll_results.json", {
      data: {
        post_id: this.model.post.id,
        poll_name: this.model.poll.name,
        user_field_name: this.groupedBy,
      },
    })
      .catch((error) => {
        if (error) {
          popupAjaxError(error);
        } else {
          this.dialog.alert(i18n("poll.error_while_fetching_voters"));
        }
      })
      .then((result) => {
        if (this.isDestroying || this.isDestroyed) {
          return;
        }

        this.set("charts", result.grouped_results);
      });
  }

  @action
  setGrouping(value) {
    this.set("groupedBy", value);
    this.fetchGroupedPollData();
  }

  @action
  onSelectPanel(panel) {
    this.set("displayMode", panel.id);
  }

  <template>
    {{! eslint-disable ember/template-no-invalid-interactive }}
    <DModal
      @title={{i18n "poll.breakdown.title"}}
      @closeModal={{@closeModal}}
      class="poll-breakdown has-tabs"
    >
      <:headerBelowTitle>
        <ul class="modal-tabs">
          <li
            class={{dConcatClass
              "modal-tab percentage"
              (if (eq this.displayMode "percentage") "is-active")
            }}
            {{on "click" (fn (mut this.displayMode) "percentage")}}
          >{{i18n "poll.breakdown.percentage"}}</li>
          <li
            class={{dConcatClass
              "modal-tab count"
              (if (eq this.displayMode "count") "is-active")
            }}
            {{on "click" (fn (mut this.displayMode) "count")}}
          >{{i18n "poll.breakdown.count"}}</li>
        </ul>
      </:headerBelowTitle>
      <:body>
        <div class="poll-breakdown-sidebar">
          <p class="poll-breakdown-title">
            {{this.title}}
          </p>

          <div class="poll-breakdown-total-votes">{{i18n
              "poll.breakdown.votes"
              count=this.model.poll.voters
            }}</div>

          <ul class="poll-breakdown-options">
            {{#each this.model.poll.options as |option index|}}
              <PollBreakdownOption
                @option={{option}}
                @index={{index}}
                @totalVotes={{this.totalVotes}}
                @optionsCount={{this.model.poll.options.length}}
                @displayMode={{this.displayMode}}
                @highlightedOption={{this.highlightedOption}}
                @onMouseOver={{fn (mut this.highlightedOption) index}}
                @onMouseOut={{fn (mut this.highlightedOption) null}}
              />
            {{/each}}
          </ul>
        </div>

        <div class="poll-breakdown-body">
          <div class="poll-breakdown-body-header">
            <label class="poll-breakdown-body-header-label">{{i18n
                "poll.breakdown.breakdown"
              }}</label>

            <ComboBox
              @content={{this.groupableUserFields}}
              @value={{this.groupedBy}}
              @nameProperty="label"
              @onChange={{this.setGrouping}}
              class="poll-breakdown-dropdown"
            />
          </div>

          <div class="poll-breakdown-charts">
            {{#each this.charts as |chart|}}
              <PollBreakdownChart
                @group={{get chart "group"}}
                @options={{get chart "options"}}
                @displayMode={{this.displayMode}}
                @highlightedOption={{this.highlightedOption}}
                @setHighlightedOption={{fn (mut this.highlightedOption)}}
              />
            {{/each}}
          </div>
        </div>
      </:body>
    </DModal>
  </template>
}
